import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { Label } from '@/core/i18n-label/type/label.type';
import { store } from '@/store';
import type { Confirmation } from '@/core/alert/interface/confirmation.interface';
import { Campaign } from '@/shared/model/campaign.model';
import type { ConfirmDialogInput } from '@/core/alert/interface/confirm-dialog-input.interface';
import type { SimpleDialogInput } from '@/core/alert/interface/simple-dialog-input.interface';
import { AlertType } from '@/core/alert/enum/alert-type.enum';
import { ApiErrorDetails } from '@/core/error/interface/api-error-details.interface';

@Module({
    dynamic: true,
    namespaced: true,
    name: 'alert',
    store,
})
export class AlertStore extends VuexModule {
    showDialog: boolean = false;

    title: Label | null = null;

    text: Label | null = null;

    type: AlertType | null = null;

    confirmKey: string | null = null;

    confirmId: string | number | null = null;

    confirmButtonLabel: Label | null = null;

    confirmButtonColor: string = AlertType.SUCCESS;

    confirmed: Confirmation | null = null;

    releaseCampaign: Campaign | null = null;

    errors: ApiErrorDetails[] = [];

    get dialogWasConfirmed(): Confirmation | null {
        return this.confirmed?.confirmed ? this.confirmed : null;
    }

    @Action
    aSimpleDialog(input: SimpleDialogInput): void {
        this.mShowDialog(input);
    }

    @Action
    aValidationDialog(errors: ApiErrorDetails[]): void {
        this.mShowValidationDialog({
            title: 'error.validation.title',
            text: 'error.validation.text',
            type: AlertType.ERROR,
            errors: errors,
        });
    }

    @Action
    aShowCampaignReleaseDialog(campaign: Campaign): void {
        this.mShowCampaignReleaseDialog(campaign);
    }

    @Action
    aConfirmDialog(input: ConfirmDialogInput): Promise<void> {
        this.mShowConfirmDialog(input);
        return new Promise<void>((resolve, reject) => {
            const unwatch = store.watch(
                () => this.dialogWasConfirmed,
                confirmation => {
                    if (confirmation && confirmation.confirmKey === input.key && confirmation.confirmId) {
                        if (confirmation.confirmed) {
                            resolve();
                        } else {
                            reject();
                        }
                        unwatch();
                    }
                }
            );
        });
    }

    @Action
    aConfirm(): void {
        if (this.confirmKey) {
            this.mConfirm();
        }
        this.mHideDialog();
    }

    @Action
    aCancel(): void {
        if (this.confirmKey) {
            this.mCancel();
        }
        this.mHideDialog();
    }

    @Mutation
    mConfirm(): void {
        this.confirmed = {
            confirmKey: this.confirmKey!,
            confirmId: this.confirmId,
            confirmed: true,
        };
        this.errors = [];
        this.confirmKey = null;
        this.confirmId = null;
    }

    @Mutation
    mCancel(): void {
        this.confirmed = {
            confirmKey: this.confirmKey!,
            confirmId: this.confirmId,
            confirmed: false,
        };
        this.errors = [];
        this.confirmKey = null;
        this.confirmId = null;
    }

    @Mutation
    mShowConfirmDialog(input: ConfirmDialogInput): void {
        this.confirmKey = input.key;
        this.confirmId = input.id || null;
        this.title = input.title;
        this.text = input.text;
        this.type = input.type;
        this.errors = [];
        this.confirmButtonLabel = input.confirmButtonLabel ?? 'defaults.confirm';
        this.confirmButtonColor = input.confirmButtonColor ?? AlertType.SUCCESS;
        this.showDialog = true;
    }

    @Mutation
    mShowDialog({ title, text, type }: SimpleDialogInput): void {
        this.title = title;
        this.text = text;
        this.type = type;
        this.errors = [];
        this.showDialog = true;
    }

    @Mutation
    mShowValidationDialog(input: SimpleDialogInput & { errors: ApiErrorDetails[] }) {
        this.title = input.title;
        this.text = input.text;
        this.type = input.type;
        this.errors = input.errors;
        this.showDialog = true;
    }

    @Mutation
    mShowCampaignReleaseDialog(campaign: Campaign): void {
        this.title = 'campaign.dialog_confirm_release.title';
        this.text = {
            i18nKey: 'campaign.dialog_confirm_release.text',
            parameters: {
                campaign: campaign.name,
            },
        };
        this.errors = [];
        this.releaseCampaign = campaign;
    }

    @Mutation
    mHideDialog(): void {
        this.releaseCampaign = null;
        this.errors = [];
        this.showDialog = false;
    }
}
